/**
 * Per-user state ends with the session (M2, L5).
 *
 * Signing out, or another user signing in in the same tab, must never leave
 * the previous person's data in memory. That matters on a shared device and
 * in the native app (one origin, no reload between accounts). One hook,
 * started in main.ts, watches the signed-in user's id and on every change
 * (sign-out included):
 *  - resets the per-user stores: timer, settings, modules, notifications,
 *    super-admin modules;
 *  - clears the RBAC notice (rbac/notices.ts). A "session revoked" notice
 *    shown just after the local sign-out survives, because it is shown after
 *    the reset, and goes at the next sign-in.
 *
 * Synchronous (flush: 'sync'), so no render can happen between the account
 * change and the reset. Stores that already follow the user themselves
 * (pins, companyProfile, the attendance review summary) are not listed.
 */
import { watch } from 'vue';
import { useAuthStore } from './auth';
import { useModulesStore, useSuperAdminModulesStore } from './modules';
import { useNotificationsStore } from './notifications';
import { useSettingsStore } from './settings';
import { useTimerStore } from './timer';
import { dismissRbacNotice } from '../rbac/notices';
import { resetAiAgentState } from '../composables/useAiAgent';

/** Empty every per-user store, the AI agent's module state and the RBAC notice. */
export function resetUserScopedState(): void {
  useTimerStore().reset();
  useSettingsStore().$reset();
  useModulesStore().$reset();
  useSuperAdminModulesStore().$reset();
  useNotificationsStore().$reset();
  resetAiAgentState();
  dismissRbacNotice();
}

/** Start following the signed-in user. Returns a stop function (tests, HMR). */
export function startSessionScope(): () => void {
  const auth = useAuthStore();
  return watch(
    () => auth.user?.id ?? null,
    (id, previous) => {
      if (id !== previous) resetUserScopedState();
    },
    { flush: 'sync' }
  );
}
